// `import type` only: it is erased at compile time, so this does NOT create a runtime
// import cycle with the screen that imports this module back.
import type { ActiveFilters } from '../screens/items.js';

/**
 * WHERE YOU WERE IN THE LIST, kept across one trip into an item.
 *
 * Field report, 30 Jul 2026: "if I searched 'polka' and opened the polka dot item, then when
 * I click back it needs to still show me the item list with the 'polka' search and items."
 * The list screen unmounts on navigation, so coming back rebuilt it from nothing: empty
 * search, no filters, scrolled to the top, and 1,940 rows between you and the garment you
 * were just looking at.
 *
 * MODULE STATE, NOT STORAGE, and that is the whole design. This is a position within one
 * session's browsing, not a preference: it should not survive a reload, a new tab, or
 * tomorrow. `sessionStorage` would make a stale search reappear after a refresh, which is
 * the opposite complaint.
 *
 * CONSUMED ON READ. Restoring is a one-shot: the trip back into the list spends it, so a
 * later arrival at the list — from the nav, from a scan, from a deep link — gets a clean
 * screen rather than someone's abandoned search. Without that, "why is my list filtered?"
 * becomes a recurring mystery with no visible cause.
 */
export interface ListPosition {
  /** What is in the search box, which may be mid-debounce and differ from `applied`. */
  query: string;
  /** The search actually applied to the rows on screen. */
  applied: string;
  filters: ActiveFilters;
  scrollY: number;
}

let remembered: ListPosition | null = null;

export function rememberListPosition(position: ListPosition): void {
  remembered = position;
}

/** Returns the saved position and forgets it. Null when there is nothing to restore. */
export function consumeListPosition(): ListPosition | null {
  const position = remembered;
  remembered = null;
  return position;
}

/** Test seam: the module keeps state across a suite, and a leftover position would make
 * the next test restore someone else's search. */
export function forgetListPosition(): void {
  remembered = null;
}
